import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import PreviewPhotoAlbum from "./PreviewPhotoAlbum";
import { useLocation } from "react-router-dom";
import PhotoAlbum from "./PhotAlbum";
import { SelectFullPt } from "../../Funcs/getPtByMrnFB";
import LoadingDots from "./LoadingDots";
import { useTranslation } from "react-i18next";
import { toEnglishNumber } from "../../Funcs/Miscellaneous";
import RepeatIcon from "../../Utils/SVGs";
import { checkCanvasState } from "../../Funcs/CapturImageFuncs";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import FbPhotoGallery from "./FbDownloadFolder";

const DocumentScanner = ({ mrn, capturedImage, onUpload }) => {
  const { t, i18n } = useTranslation();
  const offsetSelectionBox = 150;
  const [points, setPoints] = useState([
    { x: 50, y: 50 },
    { x: 300, y: 50 },
    { x: 300, y: 400 },
    { x: 50, y: 400 },
  ]);
  const [draggingPointIndex, setDraggingPointIndex] = useState(null);
  const [transformedSize, setTransformedSize] = useState({
    width: 4,
    height: 4,
  });
  const backgroundCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const resultCanvasRef = useRef(null);
  const [cvLoaded, setCvLoaded] = useState(false);
  const [uploadedImgUrl, setUploadedImgUrl] = useState("");
  const [imageSize, setImageSize] = useState(null);
  const selectionPointDiameter = 50;
  const selectionTouchAreaDiameter = 150;
  const [reset, setReset] = useState(0);
  const [showResetBtn, setShowResetBtn] = useState(false);
  const [imagesArr, setImagesArr] = useState([]);
  const imgBbApiKey = "a19dbb6469a06fee11d5b95a2e2b2318";
  const [showLoadingDots, setShowLoadingDots] = useState(false);
  const [rotation, setRotation] = useState(0); // Rotation angle state
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  // const [myCapturedImage, setMyCpturedImage] = useState(capturedImage);

  // Load OpenCV only once
  useEffect(() => {
    const loadOpenCv = () => {
      const script = document.createElement("script");
      script.src = "https://docs.opencv.org/4.x/opencv.js";
      script.async = true;
      script.onload = () => {
        const checkCvReady = setInterval(() => {
          if (window.cv && window.cv.Mat) {
            setCvLoaded(true);
            clearInterval(checkCvReady);
          }
        }, 100);
      };
      script.onerror = () => console.error("Failed to load OpenCV.js");
      document.body.appendChild(script);
    };
    if (!window.cv || !window.cv.Mat) {
      loadOpenCv();
    } else {
      setCvLoaded(true);
    }
  }, []);

  const handlePointerDown = (event) => {
    const rect = overlayCanvasRef.current.getBoundingClientRect();
    const scaleX = overlayCanvasRef.current.width / rect.width;
    const scaleY = overlayCanvasRef.current.height / rect.height;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    const pointIndex = points.findIndex(
      (point) =>
        Math.hypot(point.x - x, point.y - y) < selectionTouchAreaDiameter
    );

    if (pointIndex !== -1) {
      setDraggingPointIndex(pointIndex);
      event.preventDefault();
    }
  };

  const handlePointerMove = (event) => {
    if (draggingPointIndex === null) return;

    const rect = overlayCanvasRef.current.getBoundingClientRect();
    const scaleX = overlayCanvasRef.current.width / rect.width;
    const scaleY = overlayCanvasRef.current.height / rect.height;
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    const updatedPoints = [...points];
    updatedPoints[draggingPointIndex] = { x, y };
    setPoints(updatedPoints);
  };

  const handlePointerUp = () => {
    applyPerspectiveTransform();
    setDraggingPointIndex(null);
    setShowResetBtn(true);
  };

  useEffect(() => {
    if (cvLoaded && points) {
      applyPerspectiveTransform();
    }
  }, [cvLoaded]); // Only run when OpenCV is loaded and points change

  const applyPerspectiveTransform = () => {
    // console.log("applyPerspectiveTransform called!");
    if (!cvLoaded) return;

    const srcCanvas = backgroundCanvasRef.current;
    const src = window.cv.imread(srcCanvas);

    const srcPoints = window.cv.matFromArray(4, 1, window.cv.CV_32FC2, [
      points[0].x,
      points[0].y,
      points[1].x,
      points[1].y,
      points[2].x,
      points[2].y,
      points[3].x,
      points[3].y,
    ]);

    const widthTop = Math.hypot(
      points[1].x - points[0].x,
      points[1].y - points[0].y
    );
    const widthBottom = Math.hypot(
      points[2].x - points[3].x,
      points[2].y - points[3].y
    );
    const heightLeft = Math.hypot(
      points[3].x - points[0].x,
      points[3].y - points[0].y
    );
    const heightRight = Math.hypot(
      points[2].x - points[1].x,
      points[2].y - points[1].y
    );

    const avgWidth = Math.floor((widthTop + widthBottom) / 2);
    const avgHeight = Math.floor((heightLeft + heightRight) / 2);
    setTransformedSize({ width: avgWidth, height: avgHeight });

    const dstPoints = window.cv.matFromArray(4, 1, window.cv.CV_32FC2, [
      0,
      0,
      avgWidth,
      0,
      avgWidth,
      avgHeight,
      0,
      avgHeight,
    ]);

    const M = window.cv.getPerspectiveTransform(srcPoints, dstPoints);
    const dst = new window.cv.Mat();
    window.cv.warpPerspective(
      src,
      dst,
      M,
      new window.cv.Size(avgWidth, avgHeight)
    );
    // console.log(
    //   "Points: ",
    //   points,
    //   "srcPoints",
    //   srcPoints,
    //   "avgWidth",
    //   avgWidth,
    //   "avgHeight",
    //   avgHeight
    // );
    window.cv.imshow(resultCanvasRef.current, dst);
    // setMyCpturedImage(resultCanvasRef.current.Image);
    src.delete();
    dst.delete();
    M.delete();
    srcPoints.delete();
    dstPoints.delete();
    checkCanvasState(resultCanvasRef.current);
  };

  const drawPoints = () => {
    console.log("draw points");
    const canvas = overlayCanvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw connecting lines between points
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.closePath();
    ctx.strokeStyle = "blue";
    ctx.lineWidth = 4;
    ctx.stroke();

    // Draw each point as a larger red circle with radius 10
    points.forEach((point) => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, selectionPointDiameter, 0, 2 * Math.PI); // Updated radius to 10
      ctx.fillStyle = "red";
      ctx.fill();
      ctx.closePath();
    });
  };

  useEffect(() => {
    if (capturedImage) {
      const img = new Image();
      img.src = capturedImage;
      img.onload = () => {
        const canvas = backgroundCanvasRef.current;
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        overlayCanvasRef.current.width = img.width;
        overlayCanvasRef.current.height = img.height;
        setPoints([
          { x: offsetSelectionBox, y: offsetSelectionBox },
          { x: img.width - offsetSelectionBox, y: offsetSelectionBox },
          {
            x: img.width - offsetSelectionBox,
            y: img.height - offsetSelectionBox,
          },
          { x: offsetSelectionBox, y: img.height - offsetSelectionBox },
        ]);
      };
    }
    setDraggingPointIndex(null);
  }, [capturedImage, reset]);

  useEffect(() => drawPoints(), [points]);

  // using imgBB
  const handleModifiedUpload = () => {
    const resultCanvas = resultCanvasRef.current;
    let storedScaleFactor = localStorage.getItem("storedScaleFactor")
      ? localStorage.getItem("storedScaleFactor")
      : "0.2";

    // Prompt user to enter a quality level between 0 and 1
    let quality = prompt(`${t("EnterImageQuality")}`, storedScaleFactor);
    localStorage.setItem("storedScaleFactor", quality);
    quality = parseFloat(quality);
    quality = toEnglishNumber(quality);

    // Validate the input quality value
    if (isNaN(quality) || quality < 0 || quality > 1) {
      alert(`${t("InvalidQualityValue")}`);
      return;
    } else {
      setIsUploading(true);
    }

    // Resize the canvas for further compression if desired
    const scaleFactor = quality; // Optional: adjust this for further size reduction
    const compressedCanvas = document.createElement("canvas");
    compressedCanvas.width = resultCanvas.width * scaleFactor;
    compressedCanvas.height = resultCanvas.height * scaleFactor;
    const ctx = compressedCanvas.getContext("2d");
    ctx.drawImage(
      resultCanvas,
      0,
      0,
      compressedCanvas.width,
      compressedCanvas.height
    );

    // Convert to Blob with the specified quality
    compressedCanvas.toBlob(
      async (blob) => {
        const sizeInBytes = blob.size;
        const sizeInKB = (sizeInBytes / 1024).toFixed(2);
        const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);

        const newImageSize = sizeInMB < 1 ? `${sizeInKB} KB` : `${sizeInMB} MB`;
        setImageSize(newImageSize); // Save new size in state
        handleUpload(blob);
        // sendToImgBB(blob, newImageSize);
      },
      "image/jpeg", // Use JPEG format for compression
      quality // Use user-defined quality parameter
    );
  };

  const handleUpload = async (blob) => {
    let uploadBlob = null;
    uploadBlob = blob;
    setIsUploading(true); // Start progress bar
    const storage = getStorage();
    const fileName = `${mrn}/captured_${Date.now()}.jpg`;
    const storageRef = ref(storage, fileName);
    const uploadTask = uploadBytesResumable(storageRef, uploadBlob);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setProgress(progress);
      },
      (error) => {
        console.error("Upload failed:", error);
        alert("Upload failed!");
        setIsUploading(false); // Stop progress bar
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          alert(`Upload complete! File available at: ${downloadURL}`);
          setIsUploading(false); // Stop progress bar
          setProgress(0); // Reset progress
        });
      }
    );
  };

  // // using imgHippo
  //   const handleModifiedUpload = async () => {
  //     const resultCanvas = resultCanvasRef.current;
  //     let storedScaleFactor = localStorage.getItem("storedScaleFactor")
  //       ? localStorage.getItem("storedScaleFactor")
  //       : "0.2";
  //     // Prompt user to enter a quality level between 0 and 1
  //     let quality = prompt(
  //       "Enter image quality (between 0 and 1):",
  //       storedScaleFactor
  //     );
  //     localStorage.setItem("storedScaleFactor", quality);
  //     quality = parseFloat(quality);

  //     // Validate the input quality value
  //     if (isNaN(quality) || quality < 0 || quality > 1) {
  //       alert("Invalid quality value. Please enter a number between 0 and 1.");
  //       return;
  //     }

  //     // Resize the canvas for further compression if desired
  //     const scaleFactor = 0.7; // Optional: adjust this for further size reduction
  //     const compressedCanvas = document.createElement("canvas");
  //     compressedCanvas.width = resultCanvas.width * scaleFactor;
  //     compressedCanvas.height = resultCanvas.height * scaleFactor;
  //     const ctx = compressedCanvas.getContext("2d");
  //     ctx.drawImage(
  //       resultCanvas,
  //       0,
  //       0,
  //       compressedCanvas.width,
  //       compressedCanvas.height
  //     );

  //     // Convert to Blob with the specified quality
  //     compressedCanvas.toBlob(
  //       async (blob) => {
  //         const sizeInBytes = blob.size;
  //         const sizeInKB = (sizeInBytes / 1024).toFixed(2);
  //         const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);

  //         const newImageSize = sizeInMB < 1 ? `${sizeInKB} KB` : `${sizeInMB} MB`;
  //         setImageSize(newImageSize); // Save new size in state

  //         const formData = new FormData();
  //         formData.append("api_key", "Z20GZgFyuDvAK3CeGyOG9epwijindpia");
  //         formData.append("file", blob, "compressed_capture.jpg");

  //         try {
  //           const uploadResponse = await fetch(
  //             "https://api.imghippo.com/v1/upload",
  //             {
  //               method: "POST",
  //               body: formData,
  //             }
  //           );

  //           const uploadResult = await uploadResponse.json();
  //           if (
  //             uploadResult.success &&
  //             uploadResult.data &&
  //             uploadResult.data.url
  //           ) {
  //             setUploadedImgUrl(uploadResult.data.url);
  //             alert(
  //               `Modified image uploaded successfully! URL: ${uploadResult.data.url} \nSize: ${newImageSize}`
  //             );
  //           } else {
  //             alert("Failed to upload modified image: " + uploadResult.message);
  //           }
  //         } catch (error) {
  //           console.error("Upload failed:", error);
  //           alert("Upload failed.");
  //         }
  //       },
  //       "image/jpeg", // Use JPEG format for compression
  //       quality // Use user-defined quality parameter
  //     );
  //   };
  function findLargestNumberAfterDash(data) {
    if (!Array.isArray(data) || data.length === 0) {
      return null; // Return null if the input is invalid or empty
    }

    let maxNumber = -Infinity;

    data.forEach((item) => {
      const match = item.name.match(/-(\d+)/);
      if (match) {
        const number = parseInt(match[1], 10);
        if (!isNaN(number)) {
          maxNumber = Math.max(maxNumber, number);
        }
      }
    });

    return maxNumber === -Infinity ? 0 : maxNumber; // Return null if no valid numbers found
  }
  useEffect(() => {
    if (uploadedImgUrl) {
      const maxIdNum = findLargestNumberAfterDash(imagesArr);
      setImagesArr((prevArr) => [
        ...prevArr,
        { name: `${mrn} -${maxIdNum + 1}`, photo: uploadedImgUrl },
      ]);
      console.log("imagesArr", imagesArr);
    }
  }, [uploadedImgUrl]);

  const handleSort = (sortedPhotos) => {
    console.log("Sorted Photos:", sortedPhotos);
    setImagesArr(sortedPhotos);
  };
  const rotateAndSaveImage = () => {
    const canvas = backgroundCanvasRef.current;
    const context = canvas.getContext("2d");
    const image = new Image();

    image.onload = () => {
      // Increment the rotation state
      const newRotation = (rotation + 90) % 360;
      setRotation(newRotation);

      // Adjust canvas dimensions based on rotation
      if (newRotation % 180 === 0) {
        canvas.width = image.width;
        canvas.height = image.height;
      } else {
        canvas.width = image.height;
        canvas.height = image.width;
      }

      // Rotate the canvas
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.save();
      context.translate(canvas.width / 2, canvas.height / 2);
      context.rotate((newRotation * Math.PI) / 180); // Use the updated rotation value
      context.drawImage(
        image,
        -image.width / 2,
        -image.height / 2,
        image.width,
        image.height
      );
      context.restore();

      // Save the rotated image as a new file
      const rotatedImageUrl = canvas.toDataURL("image/jpeg");
      // setMyCpturedImage(rotatedImageUrl); // Update the displayed image
    };

    image.src = capturedImage;
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{
            touchAction: "none",
            position: "relative",
            width: "90vw",
            maxWidth: "100%",
            height: "auto",
            margin: "0 auto",
          }}
        >
          <canvas
            ref={backgroundCanvasRef}
            style={{ border: "1px solid black", width: "100%", height: "auto" }}
          />
          <canvas
            ref={overlayCanvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              border: "1px solid black",
              width: "100%",
              height: "auto",
            }}
          />
        </div>
      </div>
      <Button
        size="sm"
        variant="secondary"
        style={{ margin: "10px" }}
        onClick={rotateAndSaveImage}
      >
        {t("Rotate")} 90°
      </Button>
      {showResetBtn && (
        <Button
          size="sm"
          style={{ margin: "10px" }}
          variant="secondary"
          onClick={() => {
            setShowResetBtn(false);
            setReset(reset + 1);
          }}
        >
          {RepeatIcon("", 20)}
        </Button>
      )}

      <Button
        size="sm"
        variant="dark"
        style={{ margin: "10px" }}
        onClick={applyPerspectiveTransform}
      >
        {t("Select")}
      </Button>

      <h3>{t("TransformedImage")}:</h3>
      <canvas
        ref={resultCanvasRef}
        width={transformedSize.width}
        height={transformedSize.height}
        style={{ border: "1px solid black", width: "90vw", maxWidth: "100%" }}
      />
      {transformedSize.width > 4 && transformedSize.height > 4 && (
        <div>
          <div>
            <Button
              size="sm"
              variant="primary"
              style={{ margin: "10px" }}
              onClick={handleModifiedUpload}
            >
              {t("SaveImage")}
            </Button>
          </div>
          {isUploading && (
            <div className="mt-3">
              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                  aria-valuenow={progress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {progress}%
                </div>
              </div>
            </div>
          )}
          <a href={uploadedImgUrl}>{uploadedImgUrl}</a>
          <p>{imageSize}</p>
        </div>
      )}
      {showLoadingDots && <LoadingDots />}
    </div>
  );
};

export default DocumentScanner;
